import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import HeroSection from '@/components/HeroSection';
import Footer from '@/components/Footer';
import TestimonialsSection from '@/components/TestimonialsSection';
import ProjectGallery from '@/components/ProjectGallery';
import EnhancedQuoteCalculator from '@/components/EnhancedQuoteCalculator';
import MobileCTA from '@/components/MobileCTA';
import SEO from '@/components/SEO';
import AIReadabilityBlock from '@/components/AIReadabilityBlock';
import ExpandedFAQSection, { BilingualFAQ } from '@/components/ExpandedFAQSection';
import RelatedMaterialsLinks from '@/components/RelatedMaterialsLinks';
import { ClipboardList, MessageSquare, CalendarCheck, MapPin } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Truck, Leaf, Award, Users } from 'lucide-react';
import { trackPhoneClick } from '@/lib/analytics';
import sodInstallationImage from '@/assets/sod-installation-service.jpg';
import bulkMaterialsImage from '@/assets/bulk-materials-display.jpg';
import mulchVarietiesImage from '@/assets/mulch-varieties-display.jpg';
import premiumSoilImage from '@/assets/premium-soil-varieties.jpg';
import deliveryTruckImage from '@/assets/delivery-truck-service.jpg';
import finishedLandscapeImage from '@/assets/finished-landscape-project.jpg';
import grassVarietiesImage from '@/assets/grass-varieties-texas.jpg';

const HomePage = () => {
  const { language, t } = useLanguage();

  // SEO structured data for home page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "The Grass Depot LLC",
    "image": "https://thegrassdepotllc.com/grass-depot-logo.png",
    "telephone": "(936) 320-0763",
    "email": "info@thegrassdepotllc.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "6920 FM 1488 RD",
      "addressLocality": "Magnolia",
      "addressRegion": "TX",
      "postalCode": "77354",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "30.1588",
      "longitude": "-95.4613"
    },
    "url": "https://thegrassdepotllc.com",
    "sameAs": [
      "https://www.facebook.com/thegrassdepot",
      "https://www.instagram.com/thegrassdepot"
    ],
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "07:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "08:00",
        "closes": "16:00"
      }
    ],
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "30.1588",
        "longitude": "-95.4613"
      },
      "geoRadius": "30000"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Landscaping Materials and Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Premium Sod Installation",
            "description": "Professional sod installation services with premium grass varieties"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Bulk Material Delivery",
            "description": "Rock, mulch, soil, and sand delivery services"
          }
        }
      ]
    },
    "priceRange": "$$"
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "The Grass Depot LLC",
    "url": `https://thegrassdepotllc.com/${language}`,
    "inLanguage": language === 'en' ? 'en-US' : 'es-ES'
  };

  const homeFaqs: BilingualFAQ[] = [
    {
      q_en: 'What landscaping materials does The Grass Depot LLC sell?',
      q_es: '¿Qué materiales de paisajismo vende The Grass Depot LLC?',
      a_en: 'We sell premium sod (Bermuda, St. Augustine, Zoysia), decorative rock and gravel, organic mulch, topsoil and compost, and masonry and torpedo sand. All materials are available by the pallet, cubic yard, or bulk delivery.',
      a_es: 'Vendemos césped premium (Bermuda, San Agustín, Zoysia), roca decorativa y grava, mulch orgánico, tierra fértil y composta, y arena de mampostería y torpedo. Todos los materiales están disponibles por tarima, yarda cúbica o entrega a granel.',
    },
    {
      q_en: 'Where does The Grass Depot deliver?',
      q_es: '¿A dónde entrega The Grass Depot?',
      a_en: 'We deliver throughout Montgomery County, Texas including Magnolia, Conroe, The Woodlands, Spring, Cypress, Tomball and surrounding areas. Delivery windows are confirmed after your quote based on route and material availability.',
      a_es: 'Entregamos en todo el Condado de Montgomery, Texas incluyendo Magnolia, Conroe, The Woodlands, Spring, Cypress, Tomball y áreas cercanas. La ventana de entrega se confirma después de la cotización según ruta y disponibilidad del material.',
    },
    {
      q_en: 'How do I request a quote?',
      q_es: '¿Cómo solicito una cotización?',
      a_en: 'You can request a free quote online through our quote form, use our material calculator, or call us at (936) 320-0763. We respond quickly with pricing, delivery times, and material recommendations.',
      a_es: 'Puede solicitar una cotización gratis en línea a través de nuestro formulario, usar nuestra calculadora de materiales, o llamarnos al (936) 320-0763. Respondemos rápido con precios, tiempos de entrega y recomendaciones.',
    },
    {
      q_en: 'Can you help me calculate how much material I need?',
      q_es: '¿Pueden ayudarme a calcular cuánto material necesito?',
      a_en: 'Yes. Our online calculator estimates cubic yards, pallets, or bags based on the area and depth of your project. If you prefer, call us with your dimensions and we will size the order for you.',
      a_es: 'Sí. Nuestra calculadora en línea estima yardas cúbicas, tarimas o bolsas según el área y profundidad de su proyecto. Si prefiere, llámenos con las medidas y le calculamos el pedido.',
    },
    {
      q_en: 'Do you work with homeowners, landscapers and contractors?',
      q_es: '¿Trabajan con propietarios, jardineros y contratistas?',
      a_en: 'Yes. We serve homeowners, landscapers, builders and commercial contractors. Volume pricing and recurring delivery are available for professional accounts.',
      a_es: 'Sí. Atendemos a propietarios, jardineros, constructores y contratistas comerciales. Ofrecemos precios por volumen y entregas recurrentes para cuentas profesionales.',
    },
  ];

  const howItWorks = [
    { icon: ClipboardList, en: 'Choose your material', es: 'Elija su material', enDesc: 'Sod, rock, mulch, soil, sand or gravel.', esDesc: 'Césped, roca, mulch, tierra, arena o grava.' },
    { icon: MessageSquare, en: 'Request a quote', es: 'Solicite una cotización', enDesc: 'Online form or call (936) 320-0763.', esDesc: 'Formulario en línea o llame al (936) 320-0763.' },
    { icon: CalendarCheck, en: 'Confirm delivery', es: 'Confirme la entrega', enDesc: 'We schedule a delivery window that fits.', esDesc: 'Coordinamos una ventana de entrega que le convenga.' },
    { icon: Truck, en: 'Receive your material', es: 'Reciba su material', enDesc: 'Delivered to your driveway or job site.', esDesc: 'Entregado en su entrada o lugar de trabajo.' },
  ];

  const serviceAreas = [
    { en: 'Magnolia, TX', es: 'Magnolia, TX', slug: 'magnolia-tx' },
    { en: 'Conroe, TX', es: 'Conroe, TX', slug: 'conroe-tx' },
    { en: 'The Woodlands, TX', es: 'The Woodlands, TX', slug: 'the-woodlands-tx' },
    { en: 'Spring, TX', es: 'Spring, TX', slug: 'spring-tx' },
    { en: 'Cypress, TX', es: 'Cypress, TX', slug: 'cypress-tx' },
    { en: 'Tomball, TX', es: 'Tomball, TX', slug: 'tomball-tx' },
  ];

  const seoTitle = language === 'en'
    ? 'The Grass Depot LLC | Sod, Rock, Mulch & Soil Delivery – Montgomery County TX'
    : 'The Grass Depot LLC | Césped, Roca, Mulch y Tierra – Condado Montgomery TX';

  const seoDescription = language === 'en'
    ? 'The Grass Depot LLC delivers premium sod, rock, mulch, soil and sand across Magnolia, Conroe, The Woodlands and Montgomery County TX. Free quotes. Call (936) 320-0763.'
    : 'The Grass Depot LLC entrega césped, roca, mulch, tierra y arena premium en Magnolia, Conroe, The Woodlands y el Condado Montgomery TX. Cotizaciones gratis. Llame al (936) 320-0763.';

  const seoKeywords = language === 'en'
    ? 'Texas landscaping materials, sod installation Montgomery County, bulk delivery Texas, rock mulch soil delivery, The Woodlands landscaping, Cypress lawn care, Texas lawn installation'
    : 'materiales paisajismo Texas, instalación césped Condado Montgomery, entrega granel Texas, entrega roca mantillo tierra, paisajismo The Woodlands, cuidado césped Cypress, instalación césped Texas';

  const features = [
    {
      icon: Truck,
      title: 'Fast Delivery',
      description: 'Delivery available throughout Montgomery County',
      titleEs: 'Entrega Rápida',
      descriptionEs: 'Entrega disponible en todo el Condado de Montgomery'
    },
    {
      icon: Leaf,
      title: 'Fresh Cut from the Farm',
      description: 'Top-grade grass fresh cut from the farm, plus quality materials',
      titleEs: 'Fresco del Campo',
      descriptionEs: 'Pasto de primera calidad recién cortado del campo, más materiales de calidad'
    },
    {
      icon: Award,
      title: 'Expert Service',
      description: 'Professional landscaping expertise and guidance',
      titleEs: 'Servicio Experto',
      descriptionEs: 'Experiencia profesional en jardinería y asesoramiento'
    },
    {
      icon: Users,
      title: 'Family Owned',
      description: 'Local family business serving the community since 2025',
      titleEs: 'Negocio Familiar',
      descriptionEs: 'Negocio familiar local sirviendo a la comunidad desde 2025'
    }
  ];

  const categories = [
    {
      name: language === 'en' ? 'Grass' : 'Pasto',
      description: language === 'en' ? 'Premium sod varieties for Texas lawns' : 'Variedades premium de césped para jardines de Texas',
      href: `/${language}/${language === 'en' ? 'grass' : 'pasto'}`,
      image: grassVarietiesImage,
      color: 'from-green-500 to-green-600'
    },
    {
      name: language === 'en' ? 'Rock' : 'Roca',
      description: language === 'en' ? 'Decorative and functional rock materials' : 'Materiales de roca decorativos y funcionales',
      href: `/${language}/${language === 'en' ? 'rock' : 'roca'}`,
      image: bulkMaterialsImage,
      color: 'from-gray-500 to-gray-600'
    },
    {
      name: language === 'en' ? 'Mulch' : 'Mulch',
      description: language === 'en' ? 'Organic mulch for plant health and aesthetics' : 'Mulch orgánico para la salud de las plantas y estética',
      href: `/${language}/mulch`,
      image: mulchVarietiesImage,
      color: 'from-amber-600 to-amber-700'
    },
    {
      name: language === 'en' ? 'Soil' : 'Tierra',
      description: language === 'en' ? 'Nutrient-rich soil for healthy plant growth' : 'Tierra rica en nutrientes para el crecimiento saludable de plantas',
      href: `/${language}/${language === 'en' ? 'soil' : 'tierra'}`,
      image: premiumSoilImage,
      color: 'from-amber-800 to-amber-900'
    }
  ];

  const breadcrumbs = [
    { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={`/${language}`}
        structuredData={[structuredData, websiteSchema]}
        language={language}
        breadcrumbs={breadcrumbs}
      />
      <Header />
      
      <main>
      {/* Hablamos Español Banner - English version only */}
      {language === 'en' && (
        <div className="bg-primary text-primary-foreground py-3">
          <div className="container mx-auto px-6 text-center">
            <p className="text-lg font-semibold">
              ¡Hablamos Español! • We speak Spanish • 
              <a href={`/es`} className="ml-2 underline hover:no-underline">
                Ver sitio en Español
              </a>
            </p>
          </div>
        </div>
      )}
      <HeroSection />

      {/* AI Readability Block - Optimized for LLM/AI Search extraction */}
      <AIReadabilityBlock variant="home" />
      
      {/* Features Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Why Choose The Grass Depot?' : '¿Por Qué Elegir The Grass Depot?'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en' 
                ? 'We\'re committed to providing the highest quality landscaping materials with exceptional service'
                : 'Estamos comprometidos a proporcionar materiales de jardinería de la más alta calidad con servicio excepcional'
              }
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-natural transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-natural rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-foreground">
                  {language === 'en' ? feature.title : feature.titleEs}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'en' ? feature.description : feature.descriptionEs}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Our Products' : 'Nuestros Productos'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'Explore our comprehensive selection of premium landscaping materials'
                : 'Explore nuestra selección completa de materiales premium para jardinería'
              }
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {categories.map((category, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-natural transition-all duration-300 group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={category.image} 
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    width="634"
                    height="192"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {category.name}
                    </h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-muted-foreground mb-4">
                    {category.description}
                  </p>
                  <Link to={category.href}>
                    <Button variant="outline" className="group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      {language === 'en' ? 'View Products' : 'Ver Productos'}
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* How It Works */}
      <section className="py-16 bg-background" aria-labelledby="how-it-works-heading">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 id="how-it-works-heading" className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'How It Works' : 'Cómo Funciona'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'From selection to delivery in four simple steps.'
                : 'De la selección a la entrega en cuatro simples pasos.'}
            </p>
          </div>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step, i) => (
              <li key={i}>
                <Card className="p-6 h-full text-center">
                  <div className="w-14 h-14 bg-gradient-natural rounded-full flex items-center justify-center mx-auto mb-4">
                    <step.icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="text-sm font-semibold text-primary mb-1">
                    {language === 'en' ? `Step ${i + 1}` : `Paso ${i + 1}`}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {language === 'en' ? step.en : step.es}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {language === 'en' ? step.enDesc : step.esDesc}
                  </p>
                </Card>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Service Areas quick links */}
      <section className="py-14 bg-gradient-subtle" aria-labelledby="service-areas-heading">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 id="service-areas-heading" className="text-3xl lg:text-4xl font-bold text-foreground mb-3">
              {language === 'en' ? 'Local Delivery Areas' : 'Áreas de Entrega Local'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'Serving Magnolia, Montgomery County and surrounding Texas communities.'
                : 'Servimos a Magnolia, el Condado de Montgomery y comunidades cercanas de Texas.'}
            </p>
          </div>
          <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {serviceAreas.map((a) => (
              <Link
                key={a.slug}
                to={`/${language}/areas/${a.slug}`}
                className="flex items-center justify-center gap-2 p-3 rounded-lg border bg-card hover:border-primary hover:shadow-md transition-all text-sm font-medium text-foreground"
                data-event-name="internal_link_click"
                data-source={`home-area-${a.slug}`}
              >
                <MapPin className="h-4 w-4 text-primary" aria-hidden="true" />
                <span>{language === 'en' ? a.en : a.es}</span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link
              to={`/${language}/areas`}
              className="text-primary font-semibold hover:underline"
              data-event-name="internal_link_click"
              data-source="home-areas-all"
            >
              {language === 'en' ? 'View all service areas →' : 'Ver todas las áreas de servicio →'}
            </Link>
          </div>
        </div>
      </section>

      <EnhancedQuoteCalculator />
      <ProjectGallery />
      <TestimonialsSection />

      {/* Homepage FAQ */}
      <ExpandedFAQSection
        faqs={homeFaqs}
        language={language as 'en' | 'es'}
        id="home-faq"
      />

      {/* Related materials */}
      <RelatedMaterialsLinks language={language as 'en' | 'es'} />

      {/* Contact CTA Section */}
      <section className="py-16 bg-gradient-natural text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            {language === 'en' ? 'Ready to Transform Your Grass or Rock project?' : '¿Listo para Transformar tu Proyecto de Césped o Roca?'}
          </h2>
          <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Contact us today for expert advice and competitive pricing on all your needs'
              : 'Contáctenos hoy para asesoramiento experto y precios competitivos en todas sus necesidades'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:9363200763" data-event-name="phone_click" data-source="home_bottom_cta" onClick={() => trackPhoneClick('home_bottom_cta')}>
              <Button variant="outline" size="lg" className="bg-white/95 border-white text-primary hover:bg-white hover:text-primary">
                {t('hero.cta.call')}
              </Button>
            </a>
            <Link to={`/${language}/${language === 'en' ? 'contact' : 'contacto'}`}>
              <Button variant="secondary" size="lg">
                {language === 'en' ? 'Get in Touch' : 'Póngase en Contacto'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </main>
      
      <Footer />
      <MobileCTA />
    </div>
  );
};

export default HomePage;